import { Box, CircularProgress, Stack, ToggleButton, ToggleButtonGroup, Typography } from '@mui/material'
import { SyntheticEvent, useEffect, useMemo, useState } from 'react'
import { Link, Outlet, useNavigate } from 'react-router-dom'
import { TMediaContent } from '../../data/Media'
import { getAssessmentPointLabel, TStep } from '../../data/WorkflowSteps'
import { useAppSelector } from './monitorReduxHooks'
import { selectMediaContent } from './silceMediaContent'
import { selectTransposed } from './sliceMonitorData'
import { selectProcHeader, selectStatus as selectProcHeaderStatus } from './sliceMonitorProcHeader'

type TMediaTabValue = TStep | 'END'

export default function TabMedia() {
    const transposed = useAppSelector(selectTransposed)
    const content = useAppSelector(selectMediaContent)
    const header = useAppSelector(selectProcHeader)
    const procHeaderStatus = useAppSelector(selectProcHeaderStatus)

    const isFinished = useMemo(() => {
        if (!header?.disposition) {
            return false
        }

        return header.disposition === 'finished'
    }, [header])

    if (procHeaderStatus === 'loading' || !transposed) {
        return (
            <CircularProgress
                size='36px'
                sx={{
                    position: 'absolute',
                    top: '50%',
                    left: '50%',
                    transform: 'translate(-50%, -50%)',
                }}
            />
        )
    }

    if (!content) {
        return (
            <Box p={2}>
                <Typography variant='body1'>
                    Media content is unavailable.
                </Typography>
            </Box>
        )
    }

    return (
        <MediaTabs
            tabsNumber={transposed.dataLength + 1}
            content={content}
            isFinished={isFinished}
        />
    )
}

function MediaTabs({
    tabsNumber,
    content,
    isFinished,
}: {
    tabsNumber: number
    content: TMediaContent
    isFinished: boolean
}) {
    const navigate = useNavigate()

    const availableTabs = useMemo<TMediaTabValue[]>(() => {
        const keys = Object.keys(content).filter((_, index) => {
            return index < tabsNumber
        }) as TStep[]

        if (isFinished) {
            return [...keys, 'END']
        }

        return keys
    }, [content, tabsNumber, isFinished])

    const [tabValue, setTabValue] = useState<TMediaTabValue>(() => {
        if (availableTabs.length > 0) {
            return availableTabs[0]
        }

        return '0:00'
    })

    const handleTabValueChange = (_event: SyntheticEvent, newValue: TMediaTabValue | null) => {
        if (newValue !== null) {
            setTabValue(newValue)
        }
    }

    useEffect(() => {
        if (!availableTabs.includes(tabValue)) {
            if (availableTabs.length > 0) {
                setTabValue(availableTabs[0])
            }
        }
    }, [availableTabs, tabValue])

    useEffect(() => {
        navigate(tabValue)
    }, [navigate, tabValue])

    return (
        <Stack direction='row'>
            <ToggleButtonGroup
                orientation='vertical'
                exclusive
                onChange={handleTabValueChange}
                value={tabValue}
            >
                {availableTabs.map((step) => {
                    return (
                        <ToggleButton
                            key={step}
                            value={step}
                            to={step}
                            component={Link}
                        >
                            {step === 'END' ? 'END' : getAssessmentPointLabel(step)}
                        </ToggleButton>
                    )
                })}
            </ToggleButtonGroup>

            <Box p='5px'>
                <Outlet />
            </Box>
        </Stack>
    )
}